/**
 * Casualty Calculator Constants
 * PSP/CRE Tool v2.0 - Research-Based Factors
 * 
 * Sources:
 * - NATO AJMedP-1, AMedP-1.10 (MASCAL thresholds)
 * - US Army Iraq/Afghanistan 2003-2014 (DNBI rates)
 * - Ukraine 2022-2024 (modern warfare patterns, medical targeting)
 * - Dupuy Institute casualty analysis (base rates)
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 20, 2024
 * Version: 2.4 - ENH-018-C: Echelon support troop casualty constants (March 11, 2026)
 * Version: 2.3 - SVM-09: T1/T2 fallback constants + NATO_BASE_DOW_RATE (February 24, 2026)
 * Version: 2.2 - BCM-05/07/12/13/14/15 (February 24, 2026)
 *
 * Changes in v2.2 (February 24, 2026):
 * - BCM-05: Added `t4_percentage` field to all CASUALTY_MECHANISMS entries.
 *   Enables battleCasualtyModule to use canonical block instead of local duplicate.
 *   Added `t1_percentage` + `kia_percentage` to OTHER entry (was missing).
 * - BCM-07: Added `NIGHT_BATTLE_MODIFIERS` export (v2.1 regression — user re-uploaded
 *   v2.0). Values migrated from battleCasualtyModule.js v5.2 local const.
 * - BCM-12: Added `ENEMY_EXCHANGE_BASELINE = 0.80`.
 * - BCM-13: Added `FST_DOW_REDUCTION`, `FST_T4_CONVERSION`, `FST_T4_TO_T1_RATIO`.
 * - BCM-14: Added `GOLDEN_HOUR_BASELINE`, `GOLDEN_HOUR_KIA_FACTOR`,
 *   `GOLDEN_HOUR_CLAMP_MIN`, `GOLDEN_HOUR_CLAMP_MAX`.
 * - BCM-15: Added `SURGICAL_RATE_OVERALL`, `SURGICAL_HOURS_PER_CASE`,
 *   `SURGICAL_HOURS_PER_TEAM_DAY`.
 * - SVM-07: Added `T1_BASELINE_NO_INTERVENTION = 0.60`.
 *
 * Changes in v2.1 (February 24, 2026) — previously produced, now re-applied:
 * - BCM-07: Added `NIGHT_BATTLE_MODIFIERS` export.
 *
 * Version: 2.0 - Enhanced with phase-based rates, intensity levels, mechanism analysis
 * 
 * Changes from v1.1:
 * - Added OPERATION_PHASES with phase-specific casualty rates
 * - Added INTENSITY_LEVELS with detailed definitions
 * - Added CASUALTY_MECHANISMS from Ukraine 2022-2024
 * - Added UNIT_VULNERABILITY_MODIFIERS
 * - Added enhanced calculation functions
 * - Added DOW modifier calculator
 * - Added comprehensive validation functions
 * Updated: December 20, 2024
 */

// ============================================================================
// BASE CASUALTY RATES (DUPUY METHODOLOGY)
// ============================================================================

export const BASE_CASUALTY_RATE = {
  daily: 0.015,  // 1.5% per day (moderate intensity baseline)
  description: "Dupuy baseline for moderate-intensity conventional warfare"
};

// ============================================================================
// MASCAL THRESHOLDS (NATO AMedP-1.10)
// ============================================================================

export const MASCAL_THRESHOLDS = {
  BATTALION_ROLE1: {
    capacity_beds: 10,
    mascal_trigger_casualties: 15,  // >150% capacity in 6 hours
    time_window_hours: 6,
    rationale: "Role 1 stabilization only, limited holding capacity"
  },
  
  BRIGADE_ROLE2_BASIC: {
    capacity_beds: 50,
    capacity_surgical_teams: 2,
    mascal_trigger_casualties: 12,  // >150% surgical capacity (2 teams Ã— 4 surgeries/12h)
    time_window_hours: 12,
    rationale: "Limited surgical throughput - 2 teams = 8 surgeries max per 12h"
  },
  
  DIVISION_ROLE3: {
    capacity_beds: 44,
    capacity_surgical_teams: 8,
    mascal_trigger_casualties: 66,  // >150% capacity in 24 hours
    time_window_hours: 24,
    rationale: "Higher capacity but receives overflow from brigades"
  },
  
  CORPS_ROLE4: {
    capacity_beds: 100,
    capacity_surgical_teams: 20,
    mascal_trigger_casualties: 150,  // >150% capacity in 24 hours
    time_window_hours: 24,
    rationale: "Strategic facility, receives casualties from entire theatre"
  }
};

// ============================================================================
// DNBI RATES (Iraq/Afghanistan 2003-2014)
// Source: Hauret et al. 2016, Le et al. 2018
// ============================================================================

export const DNBI_RATES = {
  baseline_modern_operations: 0.35,  // 35% of battle casualties
  
  // Terrain multipliers (environmental disease/injury burden)
  terrain_multipliers: {
    DESERT: 0.18,      // Heat casualties, dehydration (Iraq data)
    URBAN: 0.10,       // Lower disease, higher injury risk
    MOUNTAIN: 0.16,    // Falls, altitude effects, cold weather
    JUNGLE: 0.30,      // Disease burden (historical Vietnam = 30%)
    ARCTIC: 0.25,      // Frostbite, hypothermia, environmental
    PLAINS: 0.12,      // Moderate environment
    MIXED: 0.14,       // Average of common terrain types
    OPEN: 0.11,        // Similar to plains
    FOREST: 0.15       // Between mountain and plains
  },
  
  // Duration effects (fatigue, disease accumulation)
  duration_effects: {
    days_1_7: 0.08,      // Initial period, troops fresh
    days_8_30: 0.15,     // Fatigue setting in
    days_31_90: 0.22,    // Prolonged operations
    days_90_plus: 0.28   // Extended campaigns
  },
  
  // DNBI breakdown by category
  categories: {
    musculoskeletal: 0.50,    // 50% (sprains, fractures, overuse)
    behavioral_health: 0.23,  // 23% (combat stress, PTSD, adjustment)
    disease: 0.12,            // 12% (infections, GI, respiratory)
    vehicle_accidents: 0.08,  // 8%
    other_injuries: 0.07      // 7%
  }
};

// ============================================================================
// REAR AREA CASUALTIES (Modern Warfare)
// Source: Ukraine 2022-2024, Iraq/Afghanistan NBD analysis
// ============================================================================

export const REAR_AREA_CASUALTIES = {
  // As percentage of total casualties by threat level
  PERMISSIVE: {
    rate: 0.02,  // 2% (minimal threat)
    causes: {
      accidents: 0.70,
      self_inflicted: 0.25,
      indirect_fire: 0.05
    }
  },
  
  CONTESTED: {
    rate: 0.08,  // 8% (Ukraine-level precision strikes)
    causes: {
      long_range_fires: 0.40,  // HIMARS, Iskander
      drones: 0.30,            // Loitering munitions
      accidents: 0.20,
      self_inflicted: 0.10
    }
  },
  
  DENIED: {
    rate: 0.15,  // 15% (complete threat coverage)
    causes: {
      precision_strikes: 0.50,
      drones: 0.35,
      accidents: 0.10,
      self_inflicted: 0.05
    }
  }
};

// Map threat levels to rear casualty configurations
export const THREAT_TO_REAR_MAP = {
  'LOW_THREAT': 'PERMISSIVE',
  'MODERATE_THREAT': 'CONTESTED',
  'HIGH_THREAT': 'CONTESTED',
  'EXTREME_THREAT': 'DENIED'
};

// ============================================================================
// MEDICAL PERSONNEL VULNERABILITY (Ukraine 2022-2024)
// Source: WHO verified attacks, CEPA analysis, Foreign Policy
// ============================================================================

export const MEDICAL_PERSONNEL_VULNERABILITY = {
  // Risk multipliers by role
  combat_medic_role1: {
    base_risk_multiplier: 1.15,    // 15% higher than infantry
    targeting_probability: 0.25,   // 25% chance of deliberate targeting
    survival_without_medic: 0.60,  // 60% reduction if unit loses medic
    evacuation_delay_increase: 2.5 // 2.5x longer delays
  },
  
  role2_medical_staff: {
    base_risk_multiplier: 1.08,    // 8% higher than rear area troops
    facility_strike_risk: 0.15,    // 15% probability per month
    capacity_loss_per_casualty: 0.10  // 10% capacity reduction
  },
  
  role3_medical_staff: {
    base_risk_multiplier: 1.03,    // 3% higher risk
    facility_strike_risk: 0.08     // 8% probability per month
  },
  
  ambulance_crews: {
    base_risk_multiplier: 1.20,    // 20% higher (WHO: 3x casualty rate)
    marked_vehicle_risk: 3.0,      // 3x risk if marked with red cross
    kill_zone_extension_km: 40,    // Threat extends 40km from front
    time_to_targeting_minutes: 15  // "Hit within 15 minutes if marked"
  }
};

// ============================================================================
// SURGICAL CAPACITY DEGRADATION
// ============================================================================

export const SURGICAL_CAPACITY_DEGRADATION = {
  // Personnel loss impact
  personnel_loss: {
    surgeon: {
      one_lost: -0.25,    // -25% capacity
      two_lost: -0.60,    // -60% (can't maintain 24/7)
      three_lost: -1.00   // Complete loss of surgical capability
    },
    
    anesthesiologist: {
      one_lost: -0.30,    // -30% (more critical than surgeon)
      two_lost: -0.70,
      three_lost: -1.00
    },
    
    nurse: {
      one_lost: -0.10,
      two_lost: -0.25,
      three_lost: -0.45,
      four_lost: -0.70
    }
  },
  
  // Fatigue degradation curve
  fatigue_curve: {
    hours_0_6: 1.00,
    hours_6_12: 0.90,
    hours_12_18: 0.75,
    hours_18_24: 0.60,
    hours_24_36: 0.40,
    hours_36_plus: 0.25  // Dangerous to patients
  }
};

// ============================================================================
// FATIGUE DEGRADATION BY DAY
// ============================================================================

export const FATIGUE_FACTORS = {
  days_1_3: 1.00,     // Fresh troops, high morale
  days_4_7: 1.05,     // 5% increase (fatigue starting)
  days_8_14: 1.15,    // 15% increase (cumulative fatigue)
  days_15_30: 1.30,   // 30% increase (significant degradation)
  days_30_plus: 1.40  // 40% increase (exhaustion, degraded performance)
};

export const getFatigueFactor = (day) => {
  if (day <= 3) return FATIGUE_FACTORS.days_1_3;
  if (day <= 7) return FATIGUE_FACTORS.days_4_7;
  if (day <= 14) return FATIGUE_FACTORS.days_8_14;
  if (day <= 30) return FATIGUE_FACTORS.days_15_30;
  return FATIGUE_FACTORS.days_30_plus;
};

// ============================================================================
// MODERN WARFARE MULTIPLIERS (Ukraine 2022-2024)
// ============================================================================

export const MODERN_WARFARE_MULTIPLIERS = {
  // Drone dominance factor (85% of casualties now drone-related)
  CONTESTED: {
    base_multiplier: 1.10,  // 10% increase due to drones/precision fires
    artillery_share: 0.70,   // 70% from artillery
    drone_share: 0.15,       // 15% from drones
    small_arms_share: 0.10,  // 10% from direct fire
    other_share: 0.05        // 5% from mines, IEDs, etc.
  },
  
  DENIED: {
    base_multiplier: 1.20,  // 20% increase - complete battlefield transparency
    artillery_share: 0.60,
    drone_share: 0.25,      // 25% from drones (higher in denied environment)
    small_arms_share: 0.08,
    other_share: 0.07
  }
};

// ============================================================================
// CASUALTY DISTRIBUTION RATES
// ============================================================================

export const CASUALTY_DISTRIBUTION = {
  // KIA rate (immediate death before medical care)
  kia_rate: 0.20,  // 20% die immediately
  
  // DOW rate (depends on medical quality)
  // NOT APPLIED (CALC-003 review, 2026-10-09): the survival model builds DOW from the triage mix and the
  // T1/T2 survival curves (typically 10-30% of WIA), so a 5% baseline and a 15% cap would contradict it.
  // Whether the model's DOW magnitude is plausible is an open calibration question (backlog CALC-015).
  dow_base_rate: 0.05,  // 5% baseline (good medical care - NATO standard)
  dow_rate_cap: 0.15,   // Maximum 15% even in extreme conditions

  // DOW modifiers
  // contested_environment / denied_environment: APPLIED through getParadigmDOWModifier().
  // medical_overwhelmed: NOT applied here - represented by the R2B bed-occupancy step (ENH-039,
  //   BED_OCCUPANCY_MODEL: x1.1 / x1.3 / x1.5 at 70 / 85 / 95% occupancy); applying it again would double count.
  // evacuation_delayed: NOT applied - time to surgery now enters through the T1 survival curve
  //   (CALC-001/002); a flat x1.3 for delays over 2 h would count the same effect twice.
  dow_modifiers: {
    medical_overwhelmed: 1.5,     // 50% increase if >90% MTF utilization
    evacuation_delayed: 1.3,      // 30% increase if >2 hour delay
    contested_environment: 1.4,   // 40% increase in contested
    denied_environment: 2.0       // 100% increase in denied
  },
  
  // RTD rate (return to duty from WIA)
  rtd_rate: 0.50,  // 50% return within 30-90 days (Ukraine data)
  
  // Triage distribution
  triage: {
    t1_immediate: 0.20,    // 20% (life-threatening, immediate surgery)
    t2_delayed: 0.45,      // 45% (serious but can wait)
    t3_minimal: 0.30,      // 30% (walking wounded)
    t4_expectant: 0.05     // 5% (unsurvivable)
  }
};

// ============================================================================
// PERSONNEL CASUALTY DISTRIBUTION (NEW)
// Used by personnelIntelligence.js for reserve pool calculations
// ============================================================================

export const PERSONNEL_CASUALTY_DISTRIBUTION = {
  // Distribution of casualties between combat and medical personnel
  combat_personnel_ratio: 0.90,   // 90% of casualties are combat troops
  medical_personnel_ratio: 0.10,  // 10% of casualties are medical personnel
  
  // Rationale: Medical personnel are ~5-8% of force but suffer ~10% of casualties
  // due to forward exposure (medics) and facility targeting (Ukraine data)
  description: "Based on force composition and Ukraine 2022-2024 medical targeting data"
};

// ============================================================================
// MEDICAL PERSONNEL EXPOSURE RATES (NEW)
// Daily casualty rates by exposure level
// Source: Historical data, Iraq/Afghanistan, Ukraine
// ============================================================================

export const MEDICAL_EXPOSURE_RATES = {
  HIGH: {
    rate: 0.0015,      // 0.15% per day (forward medics, same as combat troops)
    description: "Combat medics at Role 1, attached to combat units"
  },
  MEDIUM: {
    rate: 0.0007,      // 0.07% per day (Role 2 staff, CSU/PHA, HLZ personnel)
    description: "Role 2 medical staff, staging facility personnel"
  },
  LOW: {
    rate: 0.0002,      // 0.02% per day (Role 3 staff, rear areas)
    description: "Role 3 hospital staff, corps-level medical assets"
  },
  VERY_LOW: {
    rate: 0.0001,      // 0.01% per day (strategic level, Role 4)
    description: "Strategic medical facilities, national territory"
  }
};

// ============================================================================
// THREAT LEVEL MODIFIERS (NEW)
// Multipliers applied to base casualty rates based on threat environment
// ============================================================================

export const THREAT_LEVEL_MODIFIERS = {
  PERMISSIVE: {
    multiplier: 0.5,
    description: "Minimal enemy activity, secure rear area"
  },
  LOW: {
    multiplier: 0.75,
    description: "Sporadic threats, generally secure"
  },
  MODERATE: {
    multiplier: 1.0,
    description: "Standard combat operations"
  },
  HIGH: {
    multiplier: 1.5,
    description: "Intense combat, frequent engagements"
  },
  EXTREME: {
    multiplier: 2.0,
    description: "Maximum intensity, contested/denied environment"
  }
};

export const getThreatModifier = (threatLevel) => {
  return THREAT_LEVEL_MODIFIERS[threatLevel]?.multiplier || 
         THREAT_LEVEL_MODIFIERS.MODERATE.multiplier;
};

// ============================================================================
// REPLACEMENT PLANNING CONSTANTS (NEW)
// Used by personnelIntelligence.js for replacement recommendations
// ============================================================================

export const REPLACEMENT_PLANNING = {
  // When casualties typically begin (as fraction of operation duration)
  casualty_onset_fraction: 0.20,  // Casualties start ~20% into operation
  
  // Replacement wave configuration
  wave_configuration: {
    days_per_wave_coverage: 3,     // Each wave covers 3 days of projected casualties
    wave_interval_days: 7,         // Weekly replacement waves
    max_waves: 4,                  // Maximum 4 replacement waves to plan
    transport_capacity_per_vehicle: 30  // Personnel per transport vehicle
  },
  
  // Arrival time mapping (user selection â†’ days)
  arrival_time_days: {
    'Same day (organic reserves)': 0,
    '1-2 days (theater reserves)': 2,
    '2-3 days (corps reserves)': 3,
    '5-7 days from request': 7,
    '10+ days (strategic)': 12
  },
  
  // Default values
  default_arrival_days: 7,
  
  // Force strength thresholds
  minimum_strength_threshold: 0.85  // Maintain 85%+ strength
};

/**
 * Parse arrival time string to days
 * @param {string} arrivalTime - User-selected arrival time string
 * @returns {number} Days until replacement arrival
 */
export const parseArrivalTime = (arrivalTime) => {
  // Check exact matches first
  if (REPLACEMENT_PLANNING.arrival_time_days[arrivalTime] !== undefined) {
    return REPLACEMENT_PLANNING.arrival_time_days[arrivalTime];
  }
  
  // Partial matches for flexibility
  if (arrivalTime.includes('Same day')) return 0;
  if (arrivalTime.includes('1-2')) return 2;
  if (arrivalTime.includes('2-3')) return 3;
  if (arrivalTime.includes('5-7')) return 7;
  if (arrivalTime.includes('10+')) return 12;
  
  return REPLACEMENT_PLANNING.default_arrival_days;
};

// ============================================================================
// CSR THRESHOLDS (NEW)
// Combat Stress Reaction thresholds for recommendations
// ============================================================================

export const CSR_THRESHOLDS = {
  // Total CSR casualties threshold for CSC team deployment
  csc_team_deployment_threshold: 50,
  
  // Daily CSR rate threshold for enhanced support
  high_daily_rate_threshold: 10,
  
  // Description
  description: "Thresholds for Combat Stress Control team recommendations"
};

// ============================================================================
// OPERATION PHASES (NEW in v2.0)
// Phase-specific casualty rate modifiers
// ============================================================================

export const OPERATION_PHASES = {
  PREPARATION: {
    value: 'PREPARATION',
    label: 'Preparation/Staging',
    duration_typical_days: 3,
    casualty_rate_modifier: 0.10,  // 10% of base rate
    dnbi_dominant: true,
    battle_casualties: 0.05,       // 5% chance of enemy contact
    notes: 'Pre-combat phase, accidents and DNBI dominate'
  },
  
  INITIAL_ASSAULT: {
    value: 'INITIAL_ASSAULT',
    label: 'Initial Assault/Breach',
    duration_typical_hours: 24,
    casualty_rate_modifier: 2.50,  // 250% of base rate
    peak_hour_modifier: 4.00,      // First 6 hours even higher
    notes: 'Highest casualty rate, MASCAL likely'
  },
  
  EXPLOITATION: {
    value: 'EXPLOITATION',
    label: 'Exploitation/Pursuit',
    duration_typical_days: 3,
    casualty_rate_modifier: 1.50,  // 150% of base rate
    vehicle_casualty_share: 0.40,  // 40% from vehicle incidents
    notes: 'High tempo, vehicle accidents increase'
  },
  
  CONSOLIDATION: {
    value: 'CONSOLIDATION',
    label: 'Consolidation',
    duration_typical_days: 2,
    casualty_rate_modifier: 1.00,  // Baseline rate
    notes: 'Standard combat operations'
  },
  
  DEFENSE_HASTY: {
    value: 'DEFENSE_HASTY',
    label: 'Hasty Defense',
    duration_typical_days: 1,
    casualty_rate_modifier: 0.80,  // 80% of base (defender advantage)
    notes: 'Quick defensive posture'
  },
  
  DEFENSE_DELIBERATE: {
    value: 'DEFENSE_DELIBERATE',
    label: 'Deliberate Defense',
    duration_typical_days: 7,
    casualty_rate_modifier: 0.60,  // 60% of base (prepared positions)
    trench_warfare_modifier: 1.40, // If static/trench warfare
    notes: 'Prepared positions, lower casualty rate'
  },
  
  RETROGRADE: {
    value: 'RETROGRADE',
    label: 'Retrograde/Withdrawal',
    duration_typical_days: 2,
    casualty_rate_modifier: 1.80,  // 180% (disorganized, vulnerable)
    evacuation_difficulty: 2.00,   // Double evacuation time
    notes: 'High vulnerability, evacuation challenging'
  },
  
  RESERVE: {
    value: 'RESERVE',
    label: 'Reserve/Reconstitution',
    duration_typical_days: 5,
    casualty_rate_modifier: 0.15,  // 15% of base
    dnbi_dominant: true,
    psychological_recovery: true,
    notes: 'Recovery phase, DNBI and mental health focus'
  }
};

// ============================================================================
// INTENSITY LEVELS (NEW in v2.0)
// Combat intensity definitions and modifiers
// ============================================================================

export const INTENSITY_LEVELS = {
  LOW: {
    value: 'LOW',
    label: 'Low Intensity',
    daily_rate_per_1000: 0.5,      // 0.5 casualties per 1000 per day
    description: 'Sporadic contact, patrol operations',
    examples: ['Peacekeeping', 'Security operations', 'Training with risk'],
    duration_sustainable_days: 180,
    surgical_demand: 'LOW',
    mascal_probability: 0.02       // 2% daily
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate Intensity',
    daily_rate_per_1000: 2.0,      // 2 casualties per 1000 per day
    description: 'Regular contact, offensive/defensive operations',
    examples: ['Counterinsurgency', 'Limited objective attacks', 'Active defense'],
    duration_sustainable_days: 90,
    surgical_demand: 'MODERATE',
    mascal_probability: 0.10       // 10% daily
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High Intensity',
    daily_rate_per_1000: 8.0,      // 8 casualties per 1000 per day
    description: 'Sustained combat operations',
    examples: ['Major offensive', 'Deliberate attack', 'Urban operations'],
    duration_sustainable_days: 30,
    surgical_demand: 'HIGH',
    mascal_probability: 0.30       // 30% daily
  },
  
  EXTREME: {
    value: 'EXTREME',
    label: 'Extreme Intensity',
    daily_rate_per_1000: 15.0,     // 15 casualties per 1000 per day
    description: 'Peak combat, assault phase',
    examples: ['Breach operations', 'Urban assault', 'Contested crossing'],
    duration_sustainable_days: 7,
    surgical_demand: 'OVERWHELMING',
    mascal_probability: 0.60       // 60% daily
  },
  
  CATASTROPHIC: {
    value: 'CATASTROPHIC',
    label: 'Catastrophic',
    daily_rate_per_1000: 30.0,     // 30 casualties per 1000 per day
    description: 'Unit in crisis, combat ineffective soon',
    examples: ['Encirclement', 'Failed assault', 'Ambush'],
    duration_sustainable_hours: 48,
    surgical_demand: 'IMPOSSIBLE',
    mascal_probability: 0.90,      // 90% daily
    notes: 'Unit will become combat ineffective within 48-72 hours'
  }
};

// ============================================================================
// CASUALTY MECHANISMS (NEW in v2.0)
// Based on Ukraine 2022-2024 data
// ============================================================================

export const CASUALTY_MECHANISMS = {
  // Artillery remains dominant (Ukraine data)
  ARTILLERY: {
    share_of_casualties: 0.55,     // 55% of all casualties
    share_in_2022: 0.70,           // Higher in 2022 before drone surge
    share_in_2024: 0.50,           // Lower as drones increased
    injury_pattern: {
      blast: 0.60,
      fragmentation: 0.35,
      burn: 0.05
    },
    body_regions: {
      head_neck: 0.15,
      thorax: 0.20,
      abdomen: 0.20,
      extremities: 0.45
    },
    t1_percentage: 0.22,
    t4_percentage: 0.08,  // 8% T4 (expectant) — BCM-05 alias (February 24, 2026)
    kia_percentage: 0.18
  },
  
  // FPV drones rising rapidly
  FPV_DRONE: {
    share_of_casualties: 0.15,     // 15% and rising
    share_in_2022: 0.02,
    share_in_2024: 0.25,
    trend: 'RAPIDLY_INCREASING',
    injury_pattern: {
      blast: 0.50,
      fragmentation: 0.40,
      burn: 0.10
    },
    body_regions: {
      head_neck: 0.35,             // Much higher - overhead attack
      thorax: 0.20,
      abdomen: 0.15,
      extremities: 0.30
    },
    t1_percentage: 0.28,
    t4_percentage: 0.10,  // 10% T4 — head/neck wounds more lethal (BCM-05)
    kia_percentage: 0.15,
    psychological_impact: 1.30     // 30% higher psych casualties
  },
  
  // Small arms/direct fire
  SMALL_ARMS: {
    share_of_casualties: 0.12,
    injury_pattern: {
      penetrating: 0.95,
      blast: 0.00,
      fragmentation: 0.05
    },
    body_regions: {
      head_neck: 0.10,
      thorax: 0.25,
      abdomen: 0.20,
      extremities: 0.45
    },
    t1_percentage: 0.25,
    t4_percentage: 0.05,  // 5% T4 (BCM-05)
    kia_percentage: 0.20
  },
  
  // Mines and IEDs
  MINES_IED: {
    share_of_casualties: 0.10,
    injury_pattern: {
      blast: 0.70,
      fragmentation: 0.20,
      burn: 0.10
    },
    body_regions: {
      head_neck: 0.08,
      thorax: 0.15,
      abdomen: 0.15,
      extremities: 0.62           // Lower extremity dominant
    },
    amputation_rate: 0.35,        // 35% require amputation
    t1_percentage: 0.30,
    t4_percentage: 0.12,  // 12% T4 — amputations + blast (BCM-05)
    kia_percentage: 0.12
  },
  
  // Vehicle/Armor
  VEHICLE_STRIKE: {
    share_of_casualties: 0.05,
    description: 'Anti-tank weapons, tank fire, vehicle destruction',
    crew_killed_probability: 0.60,
    burn_injury_rate: 0.40,
    t1_percentage: 0.35,
    t4_percentage: 0.35,  // 35% T4 — crew casualties often unsurvivable (BCM-05)
    kia_percentage: 0.45
  },
  
  // Other (accidents, friendly fire, etc.)
  OTHER: {
    share_of_casualties: 0.03,
    t1_percentage: 0.10,  // 10% T1 (BCM-05 alias)
    t4_percentage: 0.05,  // 5% T4 (BCM-05)
    kia_percentage: 0.05, // 5% KIA
    includes: ['Accidents', 'Friendly fire', 'Falls', 'Vehicle accidents']
  }
};

// ============================================================================
// UNIT VULNERABILITY MODIFIERS (NEW in v2.0)
// Different unit types have different casualty patterns
// ============================================================================

export const UNIT_VULNERABILITY_MODIFIERS = {
  INFANTRY: {
    base_modifier: 1.00,           // Baseline
    artillery_vulnerability: 1.20, // More exposed to artillery
    drone_vulnerability: 1.30,     // Highly visible to drones
    small_arms_vulnerability: 1.00
  },
  
  ARMOR: {
    base_modifier: 0.60,           // Protected in vehicles
    artillery_vulnerability: 0.80,
    drone_vulnerability: 1.50,     // Top attack vulnerable
    vehicle_strike_vulnerability: 2.00,
    dismount_modifier: 1.40        // Higher when dismounted
  },
  
  MECHANIZED: {
    base_modifier: 0.75,
    artillery_vulnerability: 0.90,
    drone_vulnerability: 1.20,
    notes: 'Moderate protection'
  },
  
  ARTILLERY: {
    base_modifier: 0.50,           // Rear area
    counter_battery_vulnerability: 2.00,
    drone_vulnerability: 1.80,     // High value target
    notes: 'Low base rate but high when targeted'
  },
  
  ENGINEERS: {
    base_modifier: 1.20,           // Forward operations
    mine_vulnerability: 2.50,      // Breach operations
    notes: 'High risk in breach/mobility operations'
  },
  
  LOGISTICS: {
    base_modifier: 0.40,           // Rear area
    convoy_vulnerability: 1.50,    // When moving
    drone_vulnerability: 1.20,     // Supply vehicles targeted
    notes: 'Low base but convoys high risk'
  },
  
  MEDICAL: {
    base_modifier: 0.30,           // Should be protected
    targeting_modifier: 3.00,      // Ukraine: deliberate targeting
    ukraine_actual: 1.15,          // 15% higher than infantry
    notes: 'Deliberately targeted in Ukraine - higher than doctrine'
  },
  
  RECONNAISSANCE: {
    base_modifier: 1.40,           // Forward of main body
    drone_vulnerability: 1.50,
    small_arms_vulnerability: 1.30
  },
  
  AIR_DEFENSE: {
    base_modifier: 0.35,
    targeting_priority: 'HIGH',
    drone_vulnerability: 1.60,     // High value target
    notes: 'Priority target for SEAD'
  }
};

// ============================================================================
// ENHANCED HELPER FUNCTIONS (v2.0)
// ============================================================================

/**
 * Calculate phase-adjusted casualty rate
 * @param {number} baseRate - Base daily casualty rate
 */
// ============================================================================
// TRAUMA ITEM RATES
// EXT-SI-03: Proportion of casualties requiring specific consumable items.
// Used by supplyIntelligence.js calculateCasualtyDrivenConsumption() for
// trauma-specific item consumption beyond standard IV fluids / drugs.
//
// Sources:
//   NATO AMedP-9.1 Trauma Care in the Field
//   Ukraine 2022-2024 BAS consumption data
//   TCCC guidelines (JTS, CoTCCC)
//
// Note: These rates apply to BATTLE casualties (WIA), not DNBI.
//   chest_seal  — pneumothorax: ~30% of T1 have chest trauma requiring seal
//   hemostatic  — hemorrhage control: ~25% of T1+T2 require agents
//   tourniquet  — extremity wounds: ~20% of T1+T2 (Ukraine: 75% of WIA have
//                 extremity involvement but ~20% require tourniquet)
//   burn        — burn dressings: ~10% of all casualties (vehicle fires, drones)
//   splint      — fractures: ~15% of T1+T2 (extremity fractures)
// ============================================================================

export const TRAUMA_ITEM_RATES = {
  // T1 casualties only
  chest_seal_per_t1:              0.30,  // 30% of T1 have pneumothorax
  // T1 + T2 combined
  hemostatic_agent_per_t1_t2:    0.25,  // 25% need hemostatic agent
  tourniquet_per_t1_t2:          0.20,  // 20% have tourniquet-amenable wound
  splint_per_t1_t2:              0.15,  // 15% have fracture requiring splint
  // All casualties
  burn_dressing_per_casualty:    0.10,  // 10% of all casualties have burns
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get phase-adjusted casualty rate
 * @param {number} baseRate - Base casualty rate
 * @param {string} phase - Operation phase
 * @returns {number} Adjusted rate
 */
export const getPhaseAdjustedRate = (baseRate, phase) => {
  const phaseData = OPERATION_PHASES[phase];
  if (!phaseData) return baseRate;
  return baseRate * phaseData.casualty_rate_modifier;
};

/**
 * Calculate intensity-based daily casualties
 * @param {number} strength - Unit strength
 * @param {string} intensity - Intensity level
 * @returns {Object} Casualty projection
 */
export const calculateIntensityCasualties = (strength, intensity) => {
  const intData = INTENSITY_LEVELS[intensity];
  if (!intData) return { error: 'Invalid intensity level' };
  
  const dailyCasualties = Math.round(strength * intData.daily_rate_per_1000 / 1000);
  
  return {
    daily_casualties: dailyCasualties,
    weekly_casualties: dailyCasualties * 7,
    days_sustainable: intData.duration_sustainable_days || 
                      Math.round(intData.duration_sustainable_hours / 24),
    mascal_probability: intData.mascal_probability,
    surgical_demand: intData.surgical_demand
  };
};

/**
 * Calculate mechanism-based casualty distribution
 * @param {number} totalCasualties - Total casualties
 * @param {string} environment - 'UKRAINE_2024', 'CONVENTIONAL', 'ASYMMETRIC'
 * @returns {Object} Casualties by mechanism
 */
export const getCasualtyMechanismDistribution = (totalCasualties, environment = 'UKRAINE_2024') => {
  const mechanisms = CASUALTY_MECHANISMS;
  
  // Adjust shares based on environment
  let shares = {};
  if (environment === 'UKRAINE_2024') {
    shares = {
      ARTILLERY: mechanisms.ARTILLERY.share_in_2024,
      FPV_DRONE: mechanisms.FPV_DRONE.share_in_2024,
      SMALL_ARMS: mechanisms.SMALL_ARMS.share_of_casualties,
      MINES_IED: mechanisms.MINES_IED.share_of_casualties,
      VEHICLE_STRIKE: mechanisms.VEHICLE_STRIKE.share_of_casualties,
      OTHER: mechanisms.OTHER.share_of_casualties
    };
  } else {
    shares = {
      ARTILLERY: mechanisms.ARTILLERY.share_of_casualties,
      FPV_DRONE: 0.05,  // Lower in non-Ukraine
      SMALL_ARMS: 0.20, // Higher small arms
      MINES_IED: mechanisms.MINES_IED.share_of_casualties,
      VEHICLE_STRIKE: mechanisms.VEHICLE_STRIKE.share_of_casualties,
      OTHER: 0.05
    };
  }
  
  const result = {};
  let assigned = 0;
  Object.keys(shares).forEach(key => {
    result[key] = Math.round(totalCasualties * shares[key]);
    assigned += result[key];
  });
  
  // Assign remainder to largest category
  if (assigned < totalCasualties) {
    result.ARTILLERY += (totalCasualties - assigned);
  }
  
  return result;
};

/**
 * Calculate unit-type adjusted casualties
 * @param {number} baseCasualties - Base casualties
 * @param {string} unitType - Unit type
 * @param {string} primaryThreat - Primary threat mechanism
 * @returns {number} Adjusted casualties
 */
export const getUnitTypeAdjustedCasualties = (baseCasualties, unitType, primaryThreat = 'MIXED') => {
  const unitData = UNIT_VULNERABILITY_MODIFIERS[unitType];
  if (!unitData) return baseCasualties;
  
  let modifier = unitData.base_modifier;
  
  // Apply threat-specific vulnerability
  if (primaryThreat === 'ARTILLERY' && unitData.artillery_vulnerability) {
    modifier *= unitData.artillery_vulnerability;
  } else if (primaryThreat === 'DRONE' && unitData.drone_vulnerability) {
    modifier *= unitData.drone_vulnerability;
  } else if (primaryThreat === 'SMALL_ARMS' && unitData.small_arms_vulnerability) {
    modifier *= unitData.small_arms_vulnerability;
  }
  
  return Math.round(baseCasualties * modifier);
};

/**
 * Validate casualty calculation inputs
 * @param {Object} inputs - Calculation inputs
 * @returns {Object} Validation result with errors/warnings
 */
export const validateCasualtyInputs = (inputs) => {
  const errors = [];
  const warnings = [];
  
  if (!inputs.strength || inputs.strength <= 0) {
    errors.push('Unit strength must be positive');
  }
  
  if (inputs.strength > 50000) {
    warnings.push('Strength exceeds corps level - verify input');
  }
  
  if (inputs.duration && inputs.duration > 90) {
    warnings.push('Operation duration >90 days - consider phase breakdown');
  }
  
  if (inputs.intensity === 'EXTREME' && inputs.duration > 7) {
    errors.push('Extreme intensity not sustainable beyond 7 days');
  }
  
  if (inputs.intensity === 'CATASTROPHIC' && inputs.duration > 2) {
    errors.push('Catastrophic intensity not sustainable beyond 48 hours');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getDNBIRate = (terrain, day) => {
  const terrainMod = DNBI_RATES.terrain_multipliers[terrain] || DNBI_RATES.terrain_multipliers.MIXED;
  
  let durationMod;
  if (day <= 7) durationMod = DNBI_RATES.duration_effects.days_1_7;
  else if (day <= 30) durationMod = DNBI_RATES.duration_effects.days_8_30;
  else if (day <= 90) durationMod = DNBI_RATES.duration_effects.days_31_90;
  else durationMod = DNBI_RATES.duration_effects.days_90_plus;
  
  return DNBI_RATES.baseline_modern_operations * terrainMod * durationMod;
};

export const getRearCasualtyRate = (threatLevel) => {
  const rearConfig = THREAT_TO_REAR_MAP[threatLevel];
  return REAR_AREA_CASUALTIES[rearConfig]?.rate || REAR_AREA_CASUALTIES.CONTESTED.rate;
};

export const getModernWarfareMultiplier = (threatLevel) => {
  if (threatLevel === 'EXTREME_THREAT') return MODERN_WARFARE_MULTIPLIERS.DENIED.base_multiplier;
  if (threatLevel === 'HIGH_THREAT' || threatLevel === 'MODERATE_THREAT') {
    return MODERN_WARFARE_MULTIPLIERS.CONTESTED.base_multiplier;
  }
  return 1.0;  // LOW_THREAT - no modern warfare multiplier
};

// calculateDOWRate: the NATO planning-norm DOW rate (5% base, x1.4 / x2.0 by threat, capped at 15%).
// Restored 2026-10-09 after an incorrect removal. It is reported next to the model's DOW as a reference
// (results.by_brigade[*].reference_dow_rate) so planners can see how far the model is from the planning norm.
export const calculateDOWRate = (brigade, scenario) => {
  let dowRate = CASUALTY_DISTRIBUTION.dow_base_rate;
  
  // Check medical utilization (would need Tab3 data)
  // For now, use threat level as proxy
  const threatLevel = brigade?.environment?.threat_level || 'MODERATE';
  
  if (threatLevel === 'HIGH_THREAT' || threatLevel === 'MODERATE_THREAT') {
    dowRate *= CASUALTY_DISTRIBUTION.dow_modifiers.contested_environment;
  }
  
  if (threatLevel === 'EXTREME_THREAT') {
    dowRate *= CASUALTY_DISTRIBUTION.dow_modifiers.denied_environment;
  }
  
  return Math.min(dowRate, CASUALTY_DISTRIBUTION.dow_rate_cap);
};

// CALC-003 (2026-10-09): the paradigm (Tab 2) selects the documented multiplier directly.
export const PARADIGM_DOW_MODIFIER_KEY = {
  PERMISSIVE: null,                       // baseline: no multiplier
  CONTESTED: 'contested_environment',
  DENIED: 'denied_environment'
};

export const getParadigmDOWModifier = (paradigm) => {
  if (!Object.prototype.hasOwnProperty.call(PARADIGM_DOW_MODIFIER_KEY, paradigm)) {
    throw new Error(`getParadigmDOWModifier: unknown evacuation paradigm '${paradigm}' ` +
      `(valid: ${Object.keys(PARADIGM_DOW_MODIFIER_KEY).join(', ')})`);
  }
  const key = PARADIGM_DOW_MODIFIER_KEY[paradigm];
  if (key === null) return 1.0;
  const value = CASUALTY_DISTRIBUTION.dow_modifiers[key];
  if (!Number.isFinite(value)) {
    throw new Error(`getParadigmDOWModifier: CASUALTY_DISTRIBUTION.dow_modifiers.${key} is absent or not a number (got: ${value})`);
  }
  return value;
};

/**
 * Get medical exposure rate by level
 * @param {string} exposureLevel - HIGH, MEDIUM, LOW, or VERY_LOW
 * @returns {number} Daily casualty rate
 */
export const getMedicalExposureRate = (exposureLevel) => {
  return MEDICAL_EXPOSURE_RATES[exposureLevel]?.rate || 
         MEDICAL_EXPOSURE_RATES.MEDIUM.rate;
};

// ============================================================================
// BCM-07: NIGHT BATTLE MODIFIERS
// Migrated from battleCasualtyModule.js local const (February 24, 2026).
// v2.1 regression: this block was added in v2.1 but the user re-uploaded v2.0.
// Single usage in BCM: NIGHT_BATTLE_MODIFIERS.casualty_rate_modifier.
// Source: NATO STANAG 2154 night operations; Ukraine 2022-2024 after-action data.
// ============================================================================

export const NIGHT_BATTLE_MODIFIERS = {
  casualty_rate_modifier: 0.85,   // 15% fewer casualties overall (lower visibility = less effective fire)
  friendly_fire_increase: 1.50,   // 50% more friendly fire incidents (identification failures)
  evacuation_time_modifier: 1.40, // 40% longer evacuations (navigation, LZ marking, PZ marking)
  golden_hour_achievement: 0.45,  // Only 45% achieve golden hour at night (vs ~75% daytime)
  defender_advantage: 1.25,       // Defender has 25% advantage (prepared positions, pre-sited fire)
  nvg_equipped_modifier: 0.90     // NVG-equipped forces: 10% improvement on base modifiers
};

// ============================================================================
// BCM-13: FORWARD SURGICAL TEAM (FST) INTERVENTION CONSTANTS
// Replaces hardcoded literals 0.83, 0.25, 0.6 in battleCasualtyModule.js.
// Source: NATO AJMedP-2; US TCCC FST outcome research; Ukraine forward
// stabilization point data (Prytula Foundation medical reports 2022-2024).
// ============================================================================

/**
 * FST reduces Died of Wounds (DOW) rate by 17% (multiply KIA rate by this factor).
 * Research: FST reduces preventable deaths by 15-20%; 17% is NATO planning standard.
 */
export const FST_DOW_REDUCTION = 0.83;

/**
 * Proportion of T4 (expectant) casualties converted to treatable by FST presence.
 * Patients who would be expectant become salvageable with immediate damage-control surgery.
 * Research: FST converts approximately 25% of T4 to treatable categories.
 */
export const FST_T4_CONVERSION = 0.25;

/**
 * Of FST-converted T4 casualties, this fraction becomes T1 (immediate / critical-but-salvageable).
 * Remaining 40% distribute to T2/T3 via remainder calculation.
 * Source: US Army Trauma Registry; JTTS FST outcome data.
 */
export const FST_T4_TO_T1_RATIO = 0.60;

// ============================================================================
// BCM-14: GOLDEN HOUR MODIFIERS
// Replaces hardcoded literals 0.60, 0.30, 0.85, 1.15 in battleCasualtyModule.js.
// Formula: goldenHourModifier = 1.0 - ((achievement - BASELINE) * KIA_FACTOR)
//          then clamped to [CLAMP_MIN, CLAMP_MAX].
// Source: TCCC research; Blackbourne et al. 2012; Kotwal et al. 2016 (75th Ranger
// data); Ukraine 2022-2024 MEDEVAC timing analysis.
// ============================================================================

/**
 * Baseline golden hour achievement rate (60%) = 1.0x modifier (no adjustment).
 * Each 10% improvement above baseline reduces KIA by ~3% (via KIA_FACTOR).
 */
export const GOLDEN_HOUR_BASELINE = 0.60;

/**
 * KIA reduction factor per unit of golden-hour achievement above baseline.
 * Modifier = 1.0 - ((achievement - BASELINE) * KIA_FACTOR).
 * At 100% achievement: modifier = 1.0 - (0.40 * 0.30) = 0.88 (12% fewer KIA).
 */
export const GOLDEN_HOUR_KIA_FACTOR = 0.30;

/**
 * Minimum golden hour modifier (caps benefit at +15% improvement in survival).
 * Prevents over-correction when achievement approaches 100%.
 */
export const GOLDEN_HOUR_CLAMP_MIN = 0.85;

/**
 * Maximum golden hour modifier (caps penalty at +15% KIA increase).
 * Prevents catastrophic extrapolation when golden hour achievement is very low.
 */
export const GOLDEN_HOUR_CLAMP_MAX = 1.15;

// ============================================================================
// BCM-12: ENEMY EXCHANGE RATIO BASELINE
// Replaces hardcoded literal 0.8 in battleCasualtyModule.js calculateDailyBattleCasualties().
// Source: Dupuy Institute; historical combat exchange ratios in conventional peer warfare.
// Note: FORCE_SUPERIORITY.INFERIOR.enemy_casualty_multiplier = 0.80 in
// combatEffectivenessModifiers.js. The 0.8 baseline assumes the defending/inferior
// force causes fewer enemy casualties than it takes — standard attrition model.
// ============================================================================

/**
 * Enemy casualty exchange baseline before quality modifier.
 * Applied as: enemyCasualties = rawFriendlyCasualties * ENEMY_EXCHANGE_BASELINE * enemyMod
 * Value 0.80 = enemy takes 80% as many casualties as friendly (peer/slightly-inferior scenario).
 */
export const ENEMY_EXCHANGE_BASELINE = 0.80;

// ============================================================================
// BCM-15: SURGICAL CAPACITY PLANNING CONSTANTS
// Replaces hardcoded literals 0.4, 2, 8 in assessMASCALCondition().
// Source: NATO ATP 4-02.21; US AMEDD surgical throughput standards;
// Ukraine forward surgical team reports 2022-2024.
// NOTE: assessMASCALCondition() uses total casualty aggregate, not triage breakdown.
// These constants apply to the aggregate formula. If function is refactored to
// accept triage breakdown, migrate to surgicalCapacity.js SURGERY_REQUIREMENTS_BY_TRIAGE.
// ============================================================================

/**
 * Fraction of all casualties requiring surgical intervention.
 * 40% overall surgical rate: roughly T1(100%) + T2(50%) weighted by typical distribution.
 * Source: NATO ATP 4-02.21 surgical planning factors.
 */
export const SURGICAL_RATE_OVERALL = 0.40;

/**
 * Average operating room hours per surgical case.
 * Based on damage-control surgery (DCS) paradigm: abbreviated initial surgery.
 * Source: JTTS / ATLS damage-control surgery timing data.
 */
export const SURGICAL_HOURS_PER_CASE = 2;

/**
 * Available OR-hours per surgical team per day (24h operational day with team rotation).
 * 8 hours = one team rotation per day (2 surgeons + 2 nurses + 1 anesthesiologist).
 * Source: US Army FST manning; NATO Role 2E surgical team doctrine.
 */
export const SURGICAL_HOURS_PER_TEAM_DAY = 8;

// ============================================================================
// SVM-07: T1 BASELINE SURVIVAL WITHOUT INTERVENTION
// Replaces hardcoded literal 0.60 in survivalModule.js calculateTCCCEffect().
// Source: TCCC research: without trained prehospital intervention, ~60% of T1
// (IMMEDIATE) casualties survive to hospital. This is the denominator for
// measuring TCCC improvement. Interventions (tourniquet, TXA, NPA) raise this
// baseline toward the ~85-90% achievable with optimal TCCC.
// References: Kotwal et al. 2016 (75th Ranger TCCC outcomes); Holcomb et al.
// 2007 (preventable deaths analysis); TCCC CoTCCC 2024 guidelines.
// ============================================================================

/**
 * Baseline T1 (IMMEDIATE) casualty survival without any TCCC intervention.
 * Used as denominator in calculateTCCCEffect() to compute intervention benefit.
 * Value: 60% survival without trained care (40% preventable death rate).
 */
export const T1_BASELINE_NO_INTERVENTION = 0.60;

// ============================================================================
// SVM-09: NaN-GUARD FALLBACK CONSTANTS FOR SURVIVAL CALCULATIONS
// These constants are used ONLY when calculateT1/T2/T3Survival() returns
// undefined/null/NaN (BUG-057 NaN-guard paths). They are never used in
// normal operation. Values are deliberately slightly conservative relative
// to canonical survival curves to err on the side of higher mortality
// estimates in degraded-data scenarios.
// ============================================================================

/**
 * T1 (IMMEDIATE) survival starting value at 30 minutes for linear fallback formula.
 * Canonical T1_SURVIVAL_CURVE.survival_by_minutes[30] = 0.94.
 * This fallback uses 0.95 (slightly optimistic vs canonical) — retained from
 * BUG-057 original fix. Within ±1% of canonical; acceptable for NaN-guard path.
 * Source: NATO AJMedP-1 Table 3-2; golden hour doctrine.
 */
export const T1_SURVIVAL_FALLBACK_AT_30MIN = 0.95;

/**
 * T1 survival linear decay rate per minute beyond 30 minutes (fallback formula only).
 * Derived from: (0.94 at 30min) to (0.25 at 300min) ÷ 270min ≈ 0.003/min.
 * This module uses 0.005/min (steeper, more conservative) from original BUG-057 fix.
 * Source: Kotwal et al. 2016; TCCC CoTCCC decay estimates for untreated T1.
 */
export const T1_SURVIVAL_DECAY_RATE_PER_MIN = 0.005;

/**
 * T1 survival floor for NaN-guard linear fallback formula.
 * Canonical T1_SURVIVAL_CURVE.decay_parameters.minimum_survival = 0.10 (600min).
 * This fallback uses 0.50 — more optimistic, appropriate for shorter planning
 * horizons (brigade-level ops rarely exceed 300min without intervention).
 * Source: NATO planning assumption for reachable casualties.
 */
export const T1_SURVIVAL_FALLBACK_FLOOR = 0.50;

/**
 * T2 (DELAYED) survival fallback for NaN-guard path.
 * Canonical range: under_2_hours=0.98, plateau=0.96, 12h=0.94, 24h=0.91.
 * 0.95 represents mid-range (6-12h window) — conservative fallback.
 * Source: T2_SURVIVAL_CURVE.survival_windows['6_to_12_hours'].rate ≈ 0.94.
 */
export const T2_SURVIVAL_FALLBACK = 0.95;

/**
 * NATO standard base Died of Wounds (DOW) rate.
 * 12% of WIA die of wounds in absence of TCCC interventions.
 * Used as fallback when DOW calculation produces NaN (BUG-057 guard).
 * Source: NATO AJMedP-1 Section 3.4; historical US/NATO combat data;
 * JAMA Surgery 2017 (Eastridge et al. preventable death analysis).
 * Ukraine 2022-2024: 9-14% DOW rate depending on evacuation tier.
 */
export const NATO_BASE_DOW_RATE = 0.12;


// ============================================================================
// ENH-018 PHASE C: ECHELON ORGANIC SUPPORT TROOPS — CASUALTY PLANNING FACTORS
// ============================================================================
// Units: corps TSC, CAB, signal, engineer, fires brigades; division DSBs, signal,
//        engineer, artillery. Occupy CSA (corps, 40–100 km) or DSA (division,
//        15–40 km). Not in direct combat; low positional exposure.
//
// Key doctrinal assumptions (Ukraine 2022-2024 + Dupuy Institute):
//   1. Daily casualty rate = BASE_CASUALTY_RATE.daily × SUPPORT_ECHELON_CASUALTY_MULTIPLIER
//      = 0.015 × 0.20 = 0.003/day. Range 0.15–0.30× combat rate; 0.20 is the
//      conservative mid-point for DNBI-dominant rear-area populations.
//   2. DNBI dominates: 55% of losses are non-battle injuries/disease.
//      Drone/precision fires targeting of CSA/DSA accounts for remaining WIA.
//   3. Surgical fraction (60%): higher than combat average (40%) because
//      the WIA subset reaching Role 3 is precision-fire/drone-strike — penetrating
//      and blast wounds with high surgical requirement.
//   4. Role 2B bypass: support echelon casualties enter the evacuation chain
//      directly at Role 3 (no Role 2B intermediate stop). Units are co-located
//      in or near the Role 3 footprint. Implemented in Phase D.
//
// Sources: Dupuy Institute "Numbers, Predictions and War" Table 5-3;
//          Ukraine MoH SITREP 2024-Q1 (DSA targeting); AJP-4.10C §3.2.4 (CSA/DSA).
// ============================================================================

/**
 * Fractional daily casualty rate for echelon organic support troops,
 * expressed as a multiplier of BASE_CASUALTY_RATE.daily (0.015).
 *
 * Effective rate: 0.015 × 0.20 = 0.003/day.
 *
 * Do NOT hardcode 0.003 in the calculator — always derive:
 *   BASE_CASUALTY_RATE.daily × SUPPORT_ECHELON_CASUALTY_MULTIPLIER
 * This ensures the rate scales correctly if BASE_CASUALTY_RATE is ever
 * revised following a doctrine update.
 *
 * Range: 0.15–0.30× for rear-area non-combat units (Dupuy).
 * 0.20 selected as conservative mid-point (DNBI-dominant population).
 */
export const SUPPORT_ECHELON_CASUALTY_MULTIPLIER = 0.20;

/**
 * Fraction of support echelon WIA requiring surgical intervention.
 *
 * 0.60 (60%) vs SURGICAL_RATE_OVERALL (0.40 / 40%) for combat units.
 * Higher because the WIA component reaching Role 3 from CSA/DSA is
 * dominated by precision-fire and drone-strike wounds — penetrating
 * fragments and blast injuries with high surgical requirement.
 *
 * DNBI casualties (55% of total) do NOT require surgery; this fraction
 * applies to the WIA stream only.
 *
 * Source: Ukraine 2022-2024 surgical case mix analysis (DSA-origin wounds).
 */
export const SUPPORT_ECHELON_SURGICAL_FRACTION = 0.60;

/**
 * Casualty outcome distribution for echelon organic support troops.
 *
 * Reflects DNBI dominance (55%) and reduced KIA/DOW exposure vs front-line.
 * Ukraine 2022-2024: artillery/drone strikes on CSA account for WIA spike;
 * DNBI rates from Iraq/Afghanistan are consistent with rear-area populations.
 *
 * Must sum to 1.00:
 *   kia (0.08) + dow (0.04) + wia (0.33) + dnbi (0.55) = 1.00
 */
export const SUPPORT_ECHELON_CASUALTY_DISTRIBUTION = {
  kia:  0.08,   // 8%  — artillery/drone strikes on rear area
  dow:  0.04,   // 4%  — reduced: better access to Role 3 in CSA/DSA
  wia:  0.33,   // 33% — precision fires, blast, fragments
  dnbi: 0.55,   // 55% — dominant: disease, heat, non-battle injury
  rationale: 'Ukraine 2022-2024 CSA/DSA targeting + Dupuy Institute Table 5-3'
};

/**
 * Evacuation routing flag for echelon organic support casualties.
 *
 * true  = bypass Role 2B; enter evacuation chain directly at Role 3.
 * false = route through Role 2B as normal.
 *
 * Support echelon units are co-located with or adjacent to the Role 3
 * facility footprint (CSA/DSA). Routing through Role 2B would add
 * unnecessary transit distance and does not reflect actual practice.
 * AJP-4.10C §3.2.4: Corps/Division support area patients self-refer to
 * Role 3 or are transported directly without Role 2B intermediate stop.
 *
 * Consumed in Phase D (calculator routing) and Tab5 bed census.
 */
export const SUPPORT_ECHELON_BYPASSES_ROLE_2B = true;


// ============================================================================
// IMP-MARCH-001: MTF SETUP AND DISPLACEMENT PLANNING FACTORS
// ============================================================================
// Source: AJMedP-1 Section 3.4 (Deployment and Displacement of Medical Units),
//         ENH-006 specification (FUTURE_WORK.md / CURRENT_STATE Section 28).
//
// IMPORTANT: mtfCapabilities.js also contains setup_time_hours per role definition.
// Those values (R2B=4h, R2E=8h, R3=72h) are design estimates and DIFFER from
// AJMedP-1 doctrine. This file uses the doctrinal AJMedP-1 values.
// Register DOCTRINAL-005: align mtfCapabilities.js setup_time_hours with doctrine.
//
// These constants drive the bed census capacity ramp in buildBrigadeR2BBedTimeline
// and buildAggregatedBedTimeline (casualtyCalculator.js v5.41.0+).
// ============================================================================

/**
 * MTF setup time from halt until operational.
 * Applied as a calendar-day ramp after a march phase ends.
 * Role 1 is organic/vehicle-mounted  -  operational within 1h of halt (same day).
 * Role 2B leap-frogs: one section open, one setting up  -  12h for full capacity.
 * Role 2E is a larger footprint; 24h estimated (AJMedP-1 guidance).
 * Role 3 is a hospital-scale facility; 48h documented (ENH-006 / AJMedP-1).
 */
export const MTF_SETUP_TIMES_HOURS = {
  ROLE_1:  1,   // Organic to battalion  -  moves with unit; same-day operational
  ROLE_2B: 12,  // AJMedP-1 / ENH-006: leap-frog section  -  12h to full capacity
  ROLE_2E: 24,  // AJMedP-1 estimate: division-level facility
  ROLE_3:  48   // AJMedP-1 / ENH-006: corps-level hospital
};

/**
 * MTF displacement time (active pack-move-unpack window).
 * During this window the displacing element has ZERO bed capacity.
 * These are conservative estimates; exact values require AJMedP-1 Section 3.4
 * table verification  -  register as RESEARCH item if precision is needed.
 */
export const MTF_DISPLACEMENT_TIMES_HOURS = {
  ROLE_1:  2,   // Light load  -  back-of-vehicle, estimated
  ROLE_2B: 8,   // Estimated: pack surgical kit, convoy, emplace
  ROLE_2E: 16,  // Estimated: larger footprint, more vehicles
  ROLE_3:  24   // Estimated: convoy of 50+ vehicles, significant setup
};

/**
 * R2B bed capacity ramp after a march phase ends.
 * Index = days since last march day (0 = halt day itself).
 * 0.0 = MTF closed (marching or halt day, still setting up).
 * 1.0 = Full capacity restored.
 *
 * Ramp derivation — ENH-006 (12h setup target):
 *   Day 0 (halt):  0%   — MTF packed for movement
 *   Day 1 (12h):  100%  — R2B reaches full capacity within 12h (≤ 1 operational day)
 *   Day 2+:       100%  — unchanged
 * AJMedP-1 Role 2 Basic: 12h displacement setup time.
 * ENH-006: updated from [0.0, 0.5, 1.0] — previous 50% on day 1 was overcautious
 * and inconsistent with the 12h doctrinal setup time coded in MTF_SETUP_TIMES_HOURS.
 * Array length = 3 satisfies dev validation: Math.ceil(12h / 24h) + 2 = 3.
 */
export const MTF_SETUP_RAMP_R2B = [0.0, 1.0, 1.0];

/**
 * R2E bed capacity ramp after a march phase ends.
 * Division-level MTF requires 24h setup  -  no partial capacity on Day 1.
 *   Day 0 (halt):  0%  -  setting up
 *   Day 1 (12h):   0%  -  still setting up (24h total needed)
 *   Day 2+ (24h+): 100%  -  fully operational
 */
export const MTF_SETUP_RAMP_R2E = [0.0, 0.0, 1.0];

/**
 * R3 corps hospital ramp  -  not applied during tactical movement.
 * The corps-level hospital does NOT displace during brigade/division march
 * operations. It only displaces during corps-level retrograde (out of scope
 * for current movement modeling). Passing null to buildAggregatedBedTimeline
 * for R3 skips the ramp entirely (full capacity throughout).
 * Included here for completeness; not currently consumed by calculator.
 * Used if R3 displacement modeling is implemented in a future enhancement.
 */
export const MTF_SETUP_RAMP_R3  = [0.0, 0.0, 0.0, 1.0];

// ============================================================================
// GROUP-1-C: WIA:KIA RATIO VALIDATION THRESHOLDS
// ============================================================================
// Source: "WIA:KIA Ratios Modern Conflicts." Military Medicine, Vol. 187, Issue 3-4 (2022).
//         https://academic.oup.com/milmed/article/187/3-4/e313/6179198
//
// Published WIA:KIA ratios by conflict type:
//   Iraq / Afghanistan (COIN): 12.82:1 (or 9.61:1 counting DOW as KIA)
//   Peacekeeping operations:    4.46:1
//   Peer-combat / LSCO:         SIGNIFICANTLY LOWER than COIN — historically 2:1 to 6:1
//     (WWII European theater 3:1-4:1; Korea 4:1; Ukraine 2022 early data 3:1-5:1)
//
// PSP/CRE context: designed for LSCO/peer-combat planning.
// If the calculated ratio diverges ABOVE the peer-combat range, the scenario
// parameters may be misconfigured (e.g., peer scenario with COIN-era intensity
// factors, or medical structure is unrealistically reducing lethality).
// A ratio BELOW the minimum suggests extremely high lethality or poor medical care.
//
// WARNING thresholds (soft alert — does not block calculation):
//   Ratio > WIA_KIA_RATIO_HIGH_WARNING  → amber (looks like COIN, not peer combat)
//   Ratio < WIA_KIA_RATIO_LOW_WARNING   → amber (unusually high lethality)
// ============================================================================

/** Typical WIA:KIA ratio reference for Iraq/Afghanistan (COIN) — Mil Med 2022 */
export const WIA_KIA_RATIO_COIN = 12.82;

/** Typical WIA:KIA ratio for peacekeeping operations — Mil Med 2022 */
export const WIA_KIA_RATIO_PEACEKEEPING = 4.46;

/** Expected WIA:KIA range minimum for peer-combat / LSCO planning */
export const WIA_KIA_RATIO_PEER_MIN = 2.0;

/** Expected WIA:KIA range maximum for peer-combat / LSCO planning */
export const WIA_KIA_RATIO_PEER_MAX = 8.0;

/**
 * Soft warning threshold (upper): ratio above this suggests a COIN-like scenario
 * embedded in a peer-combat plan (mismatch). Value chosen between peer-max (8)
 * and COIN typical (12.82) — alert at 10:1 as a clear divergence signal.
 */
export const WIA_KIA_RATIO_HIGH_WARNING = 10.0;

/**
 * Soft warning threshold (lower): ratio below this suggests unusually high lethality
 * (potential data error or extreme LSCO conditions without adequate medical care).
 */
export const WIA_KIA_RATIO_LOW_WARNING = 1.5;
